/**
 * Add / Edit vehicle modal — thin config over the generic <FormModal>.
 * Create needs a vendor (passed in) + vehicle_number; type / facility are optional.
 * Edit allows vehicle_type / default_facility_id / status (contract rules — number is immutable).
 */
import React, { useEffect, useState } from 'react';
import { FormModal, FieldDef } from '../../../components/manpower/FormModal';
import { useVehicleMutations } from '../hooks/useVehicleMutations';
import { useFacilities } from '../../manpower-workers/hooks/useFacilities';
import type { Vehicle } from '../../../services/manpowerVehiclesApi';

interface VehicleModalProps {
	open: boolean;
	/** Vendor the new vehicle is added under (required when creating). */
	vendorId?: number;
	editing?: Vehicle | null;
	onClose: () => void;
	onSuccess: (message: string) => void;
}

export const VehicleModal: React.FC<VehicleModalProps> = ({
	open,
	vendorId,
	editing,
	onClose,
	onSuccess,
}) => {
	const isEdit = !!editing;
	const { create, update } = useVehicleMutations();
	const { data: facilities = [] } = useFacilities();

	const [values, setValues] = useState<Record<string, string | number>>({});
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setValues({
				vehicle_number: editing?.vehicle_number ?? '',
				vehicle_type: editing?.vehicle_type ?? '',
				default_facility_id: editing?.default_facility_id ?? '',
				status: editing?.status ?? 'active',
			});
			setError(null);
		}
	}, [open, editing]);

	const onChange = (name: string, value: string | number) =>
		setValues(prev => ({ ...prev, [name]: value }));

	const vehicleNumber = String(values.vehicle_number ?? '').trim();
	const numberValid = vehicleNumber.length >= 2 && vehicleNumber.length <= 50;
	// On create we need both a vendor and a valid number; on edit the number is read-only.
	const canSubmit = isEdit ? true : numberValid && vendorId != null;
	const submitting = create.isPending || update.isPending;

	const facilityChosen = values.default_facility_id !== '' && values.default_facility_id != null;

	const fields: FieldDef[] = [
		{
			name: 'vehicle_number',
			label: 'Vehicle Number',
			type: 'text',
			required: !isEdit,
			disabled: isEdit,
			placeholder: 'e.g. KA01AB1234',
			hint:
				!isEdit && !numberValid && vehicleNumber.length > 0
					? 'Vehicle number must be 2–50 characters.'
					: undefined,
		},
		{
			name: 'vehicle_type',
			label: 'Vehicle Type',
			type: 'text',
			placeholder: 'e.g. tempo, truck, auto',
		},
		{
			name: 'default_facility_id',
			label: 'Default Facility',
			type: 'select',
			placeholder: 'Select a facility…',
			options: facilities.map(f => ({ value: f.id, label: f.location })),
		},
		...(isEdit
			? [
					{
						name: 'status',
						label: 'Status',
						type: 'select' as const,
						options: [
							{ value: 'active', label: 'active' },
							{ value: 'inactive', label: 'inactive' },
						],
					},
				]
			: []),
	];

	const vehicleType = String(values.vehicle_type ?? '').trim();

	const handleSubmit = async () => {
		setError(null);
		try {
			if (isEdit && editing?.id != null) {
				await update.mutateAsync({
					id: editing.id,
					body: {
						vehicle_type: vehicleType || undefined,
						default_facility_id: facilityChosen ? Number(values.default_facility_id) : null,
						status: values.status as Vehicle['status'],
					},
				});
				onSuccess('Vehicle updated');
			} else {
				if (vendorId == null) {
					setError('Select a vendor first.');
					return;
				}
				await create.mutateAsync({
					vendorId,
					body: {
						vehicle_number: vehicleNumber,
						vehicle_type: vehicleType || undefined,
						default_facility_id: facilityChosen ? Number(values.default_facility_id) : null,
					},
				});
				onSuccess('Vehicle added');
			}
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Something went wrong');
		}
	};

	return (
		<FormModal
			open={open}
			title={isEdit ? 'Edit Vehicle' : 'Add Vehicle'}
			fields={fields}
			values={values}
			onChange={onChange}
			canSubmit={canSubmit}
			submitting={submitting}
			submitLabel={isEdit ? 'Save changes' : 'Add vehicle'}
			error={error}
			onSubmit={handleSubmit}
			onClose={onClose}
		/>
	);
};

export default VehicleModal;
